import { observable, action, decorate, computed } from 'mobx';
import api from '../../utils/api';
import { DataResponse, GenericResponse } from '../models/genericResponseModels';
import ObjHelper from '../../helper/objHelper';
import { Department } from '../models/licenseModels';

interface LicenseActionConfigDTO {
    id: string;
    question: string;
    options: LicenseOptionConfigDTO[];
    isMultiSelect: boolean;
    sortOrder: number;
}

interface LicenseOptionConfigDTO {
    id: string;
    option: string;
    sortOrder: number;
}

class LicenseFormConfigStore {
    questions: LicenseActionConfigDTO[] = [];
    initialQuestions: LicenseActionConfigDTO[] = [];
    selectedQuestionIndex: number | null = null;
    newQuestion: string = '';
    newOption: string = '';
    isQuestionDialogOpen: boolean = false;
    isOptionDialogOpen: boolean = false;
    confirmDeleteIndex: number | null = null;
    confirmDeleteOptionIndex: number | null = null;
    fetchError: string | null = null;
    saveError: string | null = null;
    saveSuccess: string | null = null;
    snackbarOpen: boolean = false;
    editQuestionIndex: number | null = null;
    editOptionIndex: number | null = null;
    editedQuestion: string = '';
    editedOption: string = '';
    departments: Department[] = [];
    selectedDepartmentId: string | null = null;
    isDepartmentDialogOpen: boolean = false;
    newDepartmentName: string = '';
    editDepartmentId: string | null = null;
    editedDepartmentName: string = '';

    async fetchData() {
        if (!this.selectedDepartmentId) return;

        try {
            const response = await api.get<DataResponse<LicenseActionConfigDTO[]>>(
                `/License/GetLicenseActionConfig?departmentId=${this.selectedDepartmentId}`
            );
            if (response.data.isSuccessful && Array.isArray(response.data.data)) {
                const data: LicenseActionConfigDTO[] = response.data.data;

                data.forEach((q, index) => {
                    if (typeof q.sortOrder !== 'number') {
                        q.sortOrder = index;
                    }
                    q.options.forEach((o, oIndex) => {
                        if (typeof o.sortOrder !== 'number') {
                            o.sortOrder = oIndex;
                        }
                    });
                });

                this.setQuestions(data);
                this.setInitialQuestions(data);
                this.setFetchError(null);
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (error) {
            this.setFetchError('Failed to fetch configuration data');
            this.setSnackbarOpen(true);
        }
    }

    setQuestions(data: LicenseActionConfigDTO[]) {
        data.forEach(question => {
            question.options.sort((a, b) => a.sortOrder - b.sortOrder);
        });
        this.questions = data.sort((a, b) => a.sortOrder - b.sortOrder);
    }

    setInitialQuestions(data: LicenseActionConfigDTO[]) {
        const sortedCopy = [...data].sort((a, b) => a.sortOrder - b.sortOrder);
        this.initialQuestions = JSON.parse(JSON.stringify(sortedCopy));
    }

    handleReorderQuestions(dragIndex: number, dropIndex: number) {
        const newQuestions = [...this.questions];
        const [movedItem] = newQuestions.splice(dragIndex, 1);
        newQuestions.splice(dropIndex, 0, movedItem);

        newQuestions.forEach((q, i) => {
            q.sortOrder = i;
        });
        this.questions = newQuestions;
    }

    handleReorderOptions(dragIndex: number, dropIndex: number) {
        if (this.selectedQuestionIndex !== null) {
            const updatedQuestions = [...this.questions];
            const options = updatedQuestions[this.selectedQuestionIndex].options;
            const [movedItem] = options.splice(dragIndex, 1);
            options.splice(dropIndex, 0, movedItem);

            options.forEach((o, i) => {
                o.sortOrder = i;
            });
            updatedQuestions[this.selectedQuestionIndex].options = options;
            this.questions = updatedQuestions;
        }
    }

    handleAddQuestion() {
        this.isQuestionDialogOpen = true;
    }

    handleConfirmAddQuestion() {
        if (this.newQuestion.trim()) {
            const maxSortOrder = this.questions.length
                ? Math.max(...this.questions.map(q => q.sortOrder)) + 1
                : 0;
            const newItem: LicenseActionConfigDTO = {
                id: '00000000-0000-0000-0000-000000000000',
                question: this.newQuestion,
                options: [],
                isMultiSelect: false,
                sortOrder: maxSortOrder
            };
            this.questions = [...this.questions, newItem].sort((a, b) => a.sortOrder - b.sortOrder);
            this.newQuestion = '';
            this.isQuestionDialogOpen = false;
        }
    }

    handleCancelAddQuestion() {
        this.newQuestion = '';
        this.isQuestionDialogOpen = false;
    }

    handleRemoveQuestion(index: number) {
        this.questions = this.questions.filter((_, i) => i !== index);
        this.selectedQuestionIndex = null;
        this.confirmDeleteIndex = null;
    }

    handleSelectQuestion(index: number) {
        this.selectedQuestionIndex = this.selectedQuestionIndex === index ? null : index;
        this.confirmDeleteIndex = null;
        this.confirmDeleteOptionIndex = null;
    }

    handleAddOption() {
        this.isOptionDialogOpen = true;
    }

    handleConfirmAddOption() {
        if (this.newOption.trim() && this.selectedQuestionIndex !== null) {
            const updatedQuestions = [...this.questions];
            const maxSortOrder = updatedQuestions[this.selectedQuestionIndex].options.length
                ? Math.max(...updatedQuestions[this.selectedQuestionIndex].options.map(o => o.sortOrder)) + 1
                : 0;
            const option: LicenseOptionConfigDTO = {
                id: '00000000-0000-0000-0000-000000000000',
                option: this.newOption,
                sortOrder: maxSortOrder
            }
            updatedQuestions[this.selectedQuestionIndex].options.push(option);
            this.questions = updatedQuestions;
            this.newOption = '';
            this.isOptionDialogOpen = false;
        }
    }

    handleCancelAddOption() {
        this.newOption = '';
        this.isOptionDialogOpen = false;
    }

    handleRemoveOption(oIndex: number) {
        if (this.selectedQuestionIndex !== null) {
            const updatedQuestions = [...this.questions];
            updatedQuestions[this.selectedQuestionIndex].options = updatedQuestions[this.selectedQuestionIndex].options.filter((_, i) => i !== oIndex);
            this.questions = updatedQuestions;
            this.confirmDeleteOptionIndex = null;
        }
    }

    handleEditQuestion(index: number) {
        this.editQuestionIndex = index;
        this.editedQuestion = this.questions[index].question;
        this.isQuestionDialogOpen = true;
    }

    handleConfirmEditQuestion() {
        if (this.editedQuestion.trim() && this.editQuestionIndex !== null) {
            this.questions[this.editQuestionIndex].question = this.editedQuestion;
            this.editQuestionIndex = null;
            this.editedQuestion = '';
            this.isQuestionDialogOpen = false;
        }
    }

    handleEditOption(qIndex: number, oIndex: number) {
        this.selectedQuestionIndex = qIndex;
        this.editOptionIndex = oIndex;
        this.editedOption = this.questions[qIndex].options[oIndex].option;
        this.isOptionDialogOpen = true;
    }

    handleConfirmEditOption() {
        if (this.editedOption.trim() && this.selectedQuestionIndex !== null && this.editOptionIndex !== null) {
            this.questions[this.selectedQuestionIndex].options[this.editOptionIndex].option = this.editedOption;
            this.editOptionIndex = null;
            this.editedOption = '';
            this.isOptionDialogOpen = false;
        }
    }

    handleCancelEdit() {
        this.editQuestionIndex = null;
        this.editOptionIndex = null;
        this.editedQuestion = '';
        this.editedOption = '';
        this.isQuestionDialogOpen = false;
        this.isOptionDialogOpen = false;
    }

    async handleSave() {
        if (!this.selectedDepartmentId) return;

        try {
            const response = await api.post<GenericResponse>('/License/SaveLicenseActionConfig', {
                departmentId: this.selectedDepartmentId,
                licenseConfig: this.questions
            });
            if (response.data.isSuccessful) {
                this.setInitialQuestions(this.questions);
                this.setSaveError(null);
                this.setSaveSuccess('Data saved successfully.');
                this.setSnackbarOpen(true);
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            this.setSaveError('Failed to save configuration');
            this.setSnackbarOpen(true);
        }
    }

    async fetchDepartments() {
        try {
            const response = await api.get<DataResponse<Department[]>>('/License/GetDepartments');
            if (response.data.isSuccessful && Array.isArray(response.data.data)) {
                this.setDepartments(response.data.data);
                if (response.data.data.length > 0 && !this.selectedDepartmentId) {
                    this.setSelectedDepartment(response.data.data[0].id);
                }
            }
        } catch (error) {
            this.setFetchError('Failed to fetch departments');
            this.setSnackbarOpen(true);
        }
    }

    setDepartments(departments: Department[]) {
        this.departments = departments;
    }

    setSelectedDepartment(departmentId: string) {
        this.selectedDepartmentId = departmentId;
        this.fetchData(); // Load config for selected department
    }

    handleAddDepartment() {
        this.newDepartmentName = '';
        this.editDepartmentId = null;
        this.isDepartmentDialogOpen = true;
    }

    handleEditDepartment(departmentId: string) {
        const department = this.departments.find(d => d.id === departmentId);
        if (department) {
            this.editDepartmentId = departmentId;
            this.editedDepartmentName = department.name;
            this.isDepartmentDialogOpen = true;
        }
    }

    async handleConfirmAddDepartment() {
        if (this.newDepartmentName.trim()) {
            try {
                const response = await api.post<DataResponse<Department>>('/License/AddDepartment', {
                    name: this.newDepartmentName
                });

                if (response.data.isSuccessful && response.data.data) {
                    const newDeptId = response.data.data;
                    this.isDepartmentDialogOpen = false;
                    this.newDepartmentName = '';
                    this.setSaveSuccess('Department added successfully');
                    this.setSnackbarOpen(true);

                    await this.fetchDepartments();
                    this.setSelectedDepartment(newDeptId);
                }
            } catch (error) {
                this.setSaveError('Failed to add department');
                this.setSnackbarOpen(true);
            }
        }
    }

    async handleConfirmEditDepartment() {
        if (this.editedDepartmentName.trim() && this.editDepartmentId) {
            try {
                const response = await api.post<GenericResponse>('/License/UpdateDepartment', {
                    id: this.editDepartmentId,
                    name: this.editedDepartmentName
                });

                if (response.data.isSuccessful) {
                    const currentDeptId = this.selectedDepartmentId;
                    this.isDepartmentDialogOpen = false;
                    this.setSaveSuccess('Department updated successfully');
                    this.setSnackbarOpen(true);

                    await this.fetchDepartments();

                    if (currentDeptId) {
                        this.setSelectedDepartment(currentDeptId);
                    }
                }
            } catch (error) {
                this.setSaveError('Failed to update department');
                this.setSnackbarOpen(true);
            }
        }
    }

    async handleRemoveDepartment(departmentId: string) {
        try {
            const response = await api.post<GenericResponse>('/License/DeleteDepartment', { id: departmentId });
            if (response.data.isSuccessful) {
                const wasSelected = this.selectedDepartmentId === departmentId;
                this.setSaveSuccess('Department removed successfully');
                this.setSnackbarOpen(true);

                await this.fetchDepartments();

                if (wasSelected) {
                    this.selectedDepartmentId = this.departments.length > 0 ? this.departments[0].id : null;
                    if (this.selectedDepartmentId) {
                        this.fetchData();
                    }
                }
            }
        } catch (error) {
            this.setSaveError('Failed to remove department');
            this.setSnackbarOpen(true);
        }
    }

    handleCancelDepartmentDialog() {
        this.isDepartmentDialogOpen = false;
        this.newDepartmentName = '';
        this.editDepartmentId = null;
        this.editedDepartmentName = '';
    }

    validateQuestions() {
        return this.questions.every((q) => q.options.length >= 2);
    }

    get isSaveEnabled() {
        return (
            !ObjHelper.deepEqual(this.questions, this.initialQuestions) &&
            this.validateQuestions()
        );
    }

    setFetchError(error: string | null) {
        this.fetchError = error;
    }

    setSnackbarOpen(open: boolean) {
        this.snackbarOpen = open;
    }

    setSaveError(error: string | null) {
        this.saveError = error;
    }

    setSaveSuccess(success: string | null) {
        this.saveSuccess = success;
    }

    setConfirmDeleteIndex(index: number | null) {
        this.confirmDeleteIndex = index;
    }

    setConfirmDeleteOptionIndex(index: number | null) {
        this.confirmDeleteOptionIndex = index;
    }

    toggleMultiSelect(index: number) {
        this.questions[index].isMultiSelect = !this.questions[index].isMultiSelect;
    }
}

decorate(LicenseFormConfigStore, {
    questions: observable,
    initialQuestions: observable,
    selectedQuestionIndex: observable,
    newQuestion: observable,
    newOption: observable,
    isQuestionDialogOpen: observable,
    isOptionDialogOpen: observable,
    confirmDeleteIndex: observable,
    confirmDeleteOptionIndex: observable,
    fetchError: observable,
    saveError: observable,
    saveSuccess: observable,
    snackbarOpen: observable,
    fetchData: action,
    setQuestions: action,
    setInitialQuestions: action,
    handleReorderQuestions: action,
    handleReorderOptions: action,
    handleAddQuestion: action,
    handleConfirmAddQuestion: action,
    handleCancelAddQuestion: action,
    handleRemoveQuestion: action,
    handleSelectQuestion: action,
    handleAddOption: action,
    handleConfirmAddOption: action,
    handleCancelAddOption: action,
    handleRemoveOption: action,
    handleSave: action,
    validateQuestions: action,
    isSaveEnabled: computed,
    setFetchError: action,
    setSaveError: action,
    setSaveSuccess: action,
    setSnackbarOpen: action,
    setConfirmDeleteIndex: action,
    setConfirmDeleteOptionIndex: action,
    toggleMultiSelect: action,
    editQuestionIndex: observable,
    editOptionIndex: observable,
    editedQuestion: observable,
    editedOption: observable,
    handleEditQuestion: action,
    handleConfirmEditQuestion: action,
    handleEditOption: action,
    handleConfirmEditOption: action,
    handleCancelEdit: action,
    departments: observable,
    selectedDepartmentId: observable,
    isDepartmentDialogOpen: observable,
    newDepartmentName: observable,
    editDepartmentId: observable,
    editedDepartmentName: observable,
    fetchDepartments: action,
    setDepartments: action,
    setSelectedDepartment: action,
    handleAddDepartment: action,
    handleEditDepartment: action,
    handleConfirmAddDepartment: action,
    handleConfirmEditDepartment: action,
    handleRemoveDepartment: action,
    handleCancelDepartmentDialog: action
});

const licenseFormConfigStore = new LicenseFormConfigStore();
export default licenseFormConfigStore;